import { DHElementsData } from '@dtx-company/app/shared/code/src/types/design-huddle.types'
import { PayloadAction, createListenerMiddleware } from '@reduxjs/toolkit'
import { RootState } from '../../types'
import {
  TemplateFilterSection,
  addSelectedCategory,
  removeSelectedCategory,
  resetSelectedCategories,
  setBatchId,
  setDhProject,
  setDhProjectIdForTemplate,
  setSelectedFlowcode
} from './printStoreSlice'
import { detailedDiff } from 'deep-object-diff'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import events from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'

export const printStoreUrlSyncMiddleware = createListenerMiddleware()

const updateSearchParam = (key: string, value: string | undefined): void => {
  const url = new URL(window.location.href)
  const existingSearchParam = url.searchParams.get(key)
  let param = value

  if (existingSearchParam && param) {
    param = existingSearchParam.includes(param)
      ? existingSearchParam.replace(param, '').replace(',,', ',').replace(/^,|,$/g, '')
      : existingSearchParam.concat(`,${param}`)
  }
  if (param) {
    url.searchParams.set(key, param)
  } else {
    url.searchParams.delete(key)
  }
  window.history.replaceState(window.history.state, '', url.toString())
}

// Add one or more listener entries that look for specific actions.
// They may contain any sync or async logic, similar to thunks.
printStoreUrlSyncMiddleware.startListening({
  actionCreator: setSelectedFlowcode,
  effect: async (action, { dispatch }) => {
    if (action.payload) {
      const batchId = getIdFromEncodedString(action.payload.id)
      dispatch(setBatchId(batchId))
    } else {
      dispatch(setBatchId(undefined))
    }
  }
})

printStoreUrlSyncMiddleware.startListening({
  actionCreator: setBatchId,
  effect: async action => {
    updateSearchParam('batchId', action.payload)
  }
})

printStoreUrlSyncMiddleware.startListening({
  actionCreator: setDhProjectIdForTemplate,
  effect: async action => {
    updateSearchParam('dhProjectId', action.payload.dhProjectId)
  }
})

printStoreUrlSyncMiddleware.startListening({
  actionCreator: addSelectedCategory,
  effect: async action => {
    if (action.payload.sectionName === TemplateFilterSection.Vertical) {
      updateSearchParam('industries', action.payload.categoryName)
    } else {
      updateSearchParam('useCases', action.payload.categoryName)
    }
  }
})

printStoreUrlSyncMiddleware.startListening({
  actionCreator: removeSelectedCategory,
  effect: async action => {
    if (action.payload.sectionName === TemplateFilterSection.Vertical) {
      updateSearchParam('industries', action.payload.categoryName)
    } else {
      updateSearchParam('useCases', action.payload.categoryName)
    }
  }
})

printStoreUrlSyncMiddleware.startListening({
  actionCreator: resetSelectedCategories,
  effect: async action => {
    if (action?.payload?.[0]?.sectionName === TemplateFilterSection.Vertical) {
      updateSearchParam('industries', '')
    } else {
      updateSearchParam('useCases', '')
    }
  }
})

export const dhProjectChangeListener = {
  actionCreator: setDhProject,
  effect: async (
    action: PayloadAction<DHElementsData>,
    { getOriginalState }: { getOriginalState: () => RootState }
  ) => {
    const {
      codeReducer: {
        printStore: { dhProject: prevDhProject, templateId, productId }
      }
    } = getOriginalState()
    if (!prevDhProject || !templateId || !productId) return // first time setting dhProject, no diff to compare

    const dhProject = action.payload
    const diff = detailedDiff(prevDhProject, dhProject)
    events.Store_Templates_TemplateEditor_Project_Data_Changed({
      templateId,
      productId,
      diff: JSON.stringify(diff)
    })
  }
}
